/**
 * Carta Estratégias — gate de leitura
 *
 * O botão "Ver artigo completo" de cada seção (e o download do PDF) abre o
 * modal de cadastro. Enviado o formulário, o backend grava o lead, devolve os
 * textos completos de todas as seções e grava o cookie de liberação — a troca
 * acontece de uma vez, em todos os textos da carta, e todos os botões somem.
 *
 * Como o texto completo é mais longo que o resumo, a troca empurra o conteúdo:
 * depois dela voltamos para o início da seção cujo botão abriu o modal.
 *
 * Numa próxima visita o PHP já renderiza tudo liberado; este módulo só cuida do
 * momento do cadastro.
 */

export function initCartaLead() {
	const modal = document.getElementById( 'carta-modal' );
	if ( ! modal ) return;

	const form       = modal.querySelector( '.carta-modal__form' );
	const painelForm = modal.querySelector( '[data-carta-modal-form]' );
	const painelOk   = modal.querySelector( '[data-carta-modal-sucesso]' );
	const feedback   = modal.querySelector( '.carta-modal__feedback' );
	const btnEnviar  = modal.querySelector( '.carta-modal__btn[type="submit"]' );
	const ajaxUrl    = modal.dataset.ajaxurl;

	if ( ! form || ! ajaxUrl ) return;

	let gatilho = null; // elemento que abriu o modal (para devolver o foco)
	let origem  = '';   // âncora da seção do botão clicado, ou 'pdf'

	const secaoPorAncora = ( anchor ) =>
		document.querySelector( `.carta-secao[data-carta-secao="${ CSS.escape( anchor ) }"]` );

	// ── Abrir / fechar ────────────────────────────────────────────────────
	const abrir = ( trigger ) => {
		gatilho = trigger;
		origem  = trigger?.dataset.cartaSecao || '';

		const campoSecao = form.querySelector( 'input[name="secao"]' );
		if ( campoSecao ) campoSecao.value = origem;

		modal.hidden = false;
		document.body.classList.add( 'carta-modal-aberto' );

		const primeiro = form.querySelector( 'input[name="nome"]' );
		if ( primeiro ) primeiro.focus();
	};

	const fechar = () => {
		modal.hidden = true;
		document.body.classList.remove( 'carta-modal-aberto' );
		if ( gatilho && document.body.contains( gatilho ) ) gatilho.focus();
		gatilho = null;
	};

	document.addEventListener( 'click', ( e ) => {
		const trigger = e.target.closest( '[data-carta-abrir-modal]' );
		if ( ! trigger ) return;

		// O botão do PDF é um <a> com href real: só interceptamos enquanto o
		// cadastro não foi feito (é quando o atributo está presente).
		e.preventDefault();
		abrir( trigger );
	} );

	modal.addEventListener( 'click', ( e ) => {
		if ( e.target.closest( '[data-carta-close]' ) ) fechar();
	} );

	document.addEventListener( 'keydown', ( e ) => {
		if ( 'Escape' === e.key && ! modal.hidden ) fechar();
	} );

	// ── "Todos" dos produtos ──────────────────────────────────────────────
	// Atalho só do front: marca/desmarca a lista inteira e fica marcado
	// quando todos os itens estão marcados um a um.
	const todos    = form.querySelector( '[data-carta-produtos-todos]' );
	const produtos = Array.from( form.querySelectorAll( 'input[name="produtos[]"]' ) );

	if ( todos ) {
		todos.addEventListener( 'change', () => {
			produtos.forEach( ( input ) => { input.checked = todos.checked; } );
		} );

		produtos.forEach( ( input ) => {
			input.addEventListener( 'change', () => {
				todos.checked = produtos.every( ( p ) => p.checked );
			} );
		} );
	}

	// ── Erros de campo ────────────────────────────────────────────────────
	const limparErros = () => {
		modal.querySelectorAll( '.carta-modal__field--erro, .carta-modal__check--erro' ).forEach( ( el ) => {
			el.classList.remove( 'carta-modal__field--erro', 'carta-modal__check--erro' );
		} );
		modal.querySelectorAll( '.carta-modal__erro' ).forEach( ( el ) => { el.textContent = ''; } );

		if ( feedback ) {
			feedback.textContent = '';
			feedback.classList.remove( 'carta-modal__feedback--visivel' );
		}
	};

	const mostrarErros = ( campos = {}, mensagem = '' ) => {
		Object.entries( campos ).forEach( ( [ campo, texto ] ) => {
			const wrapper = modal.querySelector( `[data-field="${ campo }"]` );
			if ( ! wrapper ) return;

			wrapper.classList.add(
				wrapper.classList.contains( 'carta-modal__check' )
					? 'carta-modal__check--erro'
					: 'carta-modal__field--erro'
			);

			const alvo = wrapper.querySelector( '.carta-modal__erro' );
			if ( alvo ) alvo.textContent = texto;
		} );

		if ( mensagem && feedback ) {
			feedback.textContent = mensagem;
			feedback.classList.add( 'carta-modal__feedback--visivel' );
		}
	};

	// ── Troca o resumo pelo texto completo ────────────────────────────────
	const liberarConteudos = ( conteudos ) => {
		Object.entries( conteudos || {} ).forEach( ( [ anchor, html ] ) => {
			const secao = secaoPorAncora( anchor );
			if ( ! secao ) return;

			const alvo = secao.querySelector( '[data-carta-conteudo]' );
			if ( alvo ) alvo.innerHTML = html;
		} );

		// Cadastro feito: os botões das seções não têm mais função.
		document.querySelectorAll( '[data-carta-cta]' ).forEach( ( cta ) => cta.remove() );
	};

	const liberarPdf = ( url ) => {
		const btn = document.querySelector( '[data-carta-pdf]' );
		if ( ! btn ) return null;

		btn.removeAttribute( 'data-carta-abrir-modal' );
		btn.setAttribute( 'download', '' );
		if ( url ) btn.setAttribute( 'href', url );

		return btn;
	};

	// ── Envio ─────────────────────────────────────────────────────────────
	form.addEventListener( 'submit', async ( e ) => {
		e.preventDefault();
		limparErros();

		btnEnviar.disabled    = true;
		btnEnviar.textContent = 'Enviando...';

		try {
			const dados = new FormData( form );
			dados.append( 'action', 'rb_carta_lead_submit' );

			const resposta = await fetch( ajaxUrl, { method: 'POST', body: dados, credentials: 'same-origin' } );
			const json     = await resposta.json();

			if ( ! json.success ) {
				mostrarErros( json.data?.fields, json.data?.message || 'Não foi possível concluir o cadastro.' );
				return;
			}

			liberarConteudos( json.data.conteudos );
			const btnPdf = liberarPdf( json.data.pdf );

			if ( painelForm ) painelForm.hidden = true;
			if ( painelOk )   painelOk.hidden   = false;

			if ( 'pdf' === origem && btnPdf ) {
				// O cadastro saiu do botão de download: entrega o arquivo já.
				btnPdf.click();
				fechar();
			} else if ( origem ) {
				// Volta para o início do texto do botão clicado — agora completo.
				// O foco iria para o botão, que já não existe: fica na seção.
				gatilho = null;
				fechar();
				secaoPorAncora( origem )?.scrollIntoView();
			}
		} catch ( err ) {
			mostrarErros( {}, 'Não foi possível concluir o cadastro. Tente novamente.' );
		} finally {
			btnEnviar.disabled    = false;
			btnEnviar.textContent = 'Liberar leitura';
		}
	} );
}
